import {
  IonButton,
  IonButtons,
  IonCard,
  IonCardContent,
  IonContent,
  IonHeader,
  IonInput,
  IonModal,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonSelect,
  IonSelectOption,
  IonSpinner,
  IonTextarea,
  IonTitle,
  IonToolbar,
  useIonAlert,
  useIonToast,
  useIonViewWillEnter,
} from "@ionic/react";
import { Chip, CircularProgress, TextField } from "@material-ui/core";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import { useCallback, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Header from "src/components/Header";
import {
  BuscadorRemoto,
  campoEstilo,
  Etiqueta,
  EstadoChip,
  fmtFecha,
  fmtFechaHora,
  isoHoyMas,
  MultiSelect,
  RadioOpciones,
  SI_NO,
  TablaPersonas,
} from "src/components/SolicitudCapacitacionCampos";
import SolicitudCapacitacionAPI, {
  CatalogoSolicitud,
  OperarioBusqueda,
  OpcionCatalogo,
  PuntoServicio,
  SolicitudCapacitacion,
} from "src/services/solicitudCapacitacion";

type Tab = "nueva" | "mis" | "asignadas";

const bordeInput: React.CSSProperties = { border: "1px solid #ccc", borderRadius: 4, paddingLeft: 8 };

const errorDe = (err: any, defecto: string) => err?.response?.data?.error || defecto;

const resumenPersonas = (personas?: OperarioBusqueda[]) => {
  const lista = personas || [];
  if (!lista.length) return "-";
  const primero = `${lista[0].legajo} - ${lista[0].nombre}`;
  return lista.length === 1 ? primero : `${primero} y ${lista.length - 1} más`;
};

const SolicitudesCapacitacion: React.FC = () => {
  const [present] = useIonToast();
  const [presentAlert] = useIonAlert();

  const [rol, setRol] = useState<{ solicitante: boolean; capacitador: boolean } | null>(null);
  const [tab, setTab] = useState<Tab>("nueva");
  const [catalogo, setCatalogo] = useState<CatalogoSolicitud | null>(null);

  // --- Formulario ---
  const [dirigidoA, setDirigidoA] = useState("OPERATIVO");
  const [tipo, setTipo] = useState<string | null>("INDIVIDUAL");
  const [personas, setPersonas] = useState<OperarioBusqueda[]>([]);
  const [ps, setPs] = useState<PuntoServicio | null>(null);
  const [autorizado, setAutorizado] = useState<string | null>(null);
  const [fecha, setFecha] = useState("");
  const [hora, setHora] = useState("");
  const [capacitaciones, setCapacitaciones] = useState<OpcionCatalogo[]>([]);
  const [documentos, setDocumentos] = useState<OpcionCatalogo[]>([]);
  const [comentarios, setComentarios] = useState("");
  const [enviando, setEnviando] = useState(false);

  // --- Listados ---
  const [mis, setMis] = useState<SolicitudCapacitacion[]>([]);
  const [asignadas, setAsignadas] = useState<SolicitudCapacitacion[]>([]);
  const [cargandoLista, setCargandoLista] = useState(false);
  const [detalle, setDetalle] = useState<SolicitudCapacitacion | null>(null);

  // --- Respuesta a propuesta ---
  const [rechazando, setRechazando] = useState(false);
  const [respComentario, setRespComentario] = useState("");
  const [respFecha, setRespFecha] = useState("");
  const [respHora, setRespHora] = useState("");
  const [respondiendo, setRespondiendo] = useState(false);

  const manana = isoHoyMas(1);
  const hoy = isoHoyMas(0);
  const location = useLocation();
  const history = useHistory();
  const irACierre = (solicitudId: number) => history.push(`/solicitudes-capacitacion/${solicitudId}/cierre`);

  useEffect(() => {
    Promise.all([SolicitudCapacitacionAPI.rol(), SolicitudCapacitacionAPI.catalogo()])
      .then(([r, c]) => {
        const roles = r.data.datos;
        setRol(roles);
        setCatalogo(c.data.datos);
        if (!roles.solicitante && roles.capacitador) setTab("asignadas");
      })
      .catch((err) => present({ message: errorDe(err, "No se pudo cargar el módulo"), duration: 4000, color: "danger" }));
  }, [present]);

  // Deep link desde la card del inicio / push: ?tab=asignadas&id=N abre la
  // pestaña y el detalle de esa capacitación.
  useEffect(() => {
    if (!rol) return;
    const params = new URLSearchParams(location.search);
    const tabUrl = params.get("tab") as Tab | null;
    if (tabUrl === "asignadas" && rol.capacitador) setTab("asignadas");
    else if (tabUrl === "mis" && rol.solicitante) setTab("mis");
    const id = Number(params.get("id"));
    if (id) {
      SolicitudCapacitacionAPI.obtener(id)
        .then((r) => {
          setRechazando(false);
          setDetalle(r.data.datos);
        })
        .catch((err) => present({ message: errorDe(err, "No se pudo abrir la solicitud"), duration: 4000, color: "danger" }));
    }
  }, [location.search, rol, present]);

  const cargarLista = useCallback(async () => {
    if (tab === "nueva") return;
    setCargandoLista(true);
    try {
      if (tab === "mis") {
        setMis((await SolicitudCapacitacionAPI.misSolicitudes()).data.datos || []);
      } else {
        setAsignadas((await SolicitudCapacitacionAPI.asignadas()).data.datos || []);
      }
    } catch (err) {
      present({ message: errorDe(err, "No se pudieron cargar las solicitudes"), duration: 4000, color: "danger" });
    } finally {
      setCargandoLista(false);
    }
  }, [tab, present]);

  useEffect(() => {
    cargarLista();
  }, [cargarLista]);

  // Al volver de la pantalla de cierre (u otra ruta) se refresca la lista.
  useIonViewWillEnter(() => {
    cargarLista();
  }, [cargarLista]);

  const limpiarFormulario = () => {
    setTipo("INDIVIDUAL");
    setPersonas([]);
    setPs(null);
    setAutorizado(null);
    setFecha("");
    setHora("");
    setCapacitaciones([]);
    setDocumentos([]);
    setComentarios("");
  };

  const enviar = async () => {
    let error: string | null = null;
    if (!tipo) error = "Seleccione el tipo de capacitación";
    else if (tipo === "INDIVIDUAL" && !personas.length) error = "Seleccione al menos un operario o supervisor a capacitar";
    else if (!ps) error = "Seleccione el punto de servicio";
    else if (!autorizado) error = "Indique si está autorizado por el cliente";
    else if (fecha && fecha < manana) error = "La fecha sugerida debe ser posterior a hoy";
    else if (!hora) error = "Indique el horario sugerido";
    else if (!capacitaciones.length) error = "Seleccione al menos una capacitación requerida";
    else if (!comentarios.trim()) error = "Complete los comentarios adicionales";
    if (error) {
      present({ message: error, duration: 3000, color: "warning" });
      return;
    }
    setEnviando(true);
    try {
      const r = await SolicitudCapacitacionAPI.crear({
        dirigido_a: dirigidoA,
        tipo: tipo as "INDIVIDUAL" | "PS",
        personas: tipo === "INDIVIDUAL" ? personas.map((p) => Number(p.legajo)) : [],
        ps_id: ps!.id,
        autorizado_cliente: autorizado === "SI",
        fecha_sugerida: fecha || null,
        hora_sugerida: hora,
        capacitaciones: capacitaciones.map((c) => c.id),
        documentos: documentos.map((d) => d.id),
        comentarios: comentarios.trim(),
      });
      present({ message: r.data.info || "Solicitud enviada", duration: 3000, color: "success" });
      limpiarFormulario();
      setTab("mis");
    } catch (err) {
      present({ message: errorDe(err, "No se pudo enviar la solicitud"), duration: 4000, color: "danger" });
    } finally {
      setEnviando(false);
    }
  };

  const abrirDetalle = async (s: SolicitudCapacitacion) => {
    setRechazando(false);
    setRespComentario("");
    setRespFecha("");
    setRespHora("");
    setDetalle(s);
    try {
      setDetalle((await SolicitudCapacitacionAPI.obtener(s.solicitud_id)).data.datos);
    } catch {
      /* se queda con los datos del listado */
    }
  };

  const responder = async (acepta: boolean) => {
    if (!detalle) return;
    if (!acepta && !respComentario.trim()) {
      present({ message: "Indique por qué no acepta la propuesta", duration: 3000, color: "warning" });
      return;
    }
    if (!acepta && respFecha && respFecha < manana) {
      present({ message: "La nueva fecha debe ser posterior a hoy", duration: 3000, color: "warning" });
      return;
    }
    setRespondiendo(true);
    try {
      const r = await SolicitudCapacitacionAPI.responder(detalle.solicitud_id, {
        acepta,
        comentario: respComentario.trim() || undefined,
        fecha_sugerida: !acepta && respFecha ? respFecha : undefined,
        hora_sugerida: !acepta && respHora ? respHora : undefined,
      });
      present({ message: r.data.info, duration: 3000, color: "success" });
      setDetalle(null);
      cargarLista();
    } catch (err) {
      present({ message: errorDe(err, "No se pudo enviar la respuesta"), duration: 4000, color: "danger" });
    } finally {
      setRespondiendo(false);
    }
  };

  const cancelar = () => {
    if (!detalle) return;
    presentAlert({
      header: "Cancelar solicitud",
      message: `¿Cancelar la solicitud N° ${detalle.solicitud_id}?`,
      buttons: [
        "No",
        {
          text: "Sí, cancelar",
          handler: async () => {
            try {
              await SolicitudCapacitacionAPI.cancelar(detalle.solicitud_id);
              present({ message: "Solicitud cancelada", duration: 3000, color: "success" });
              setDetalle(null);
              cargarLista();
            } catch (err) {
              present({ message: errorDe(err, "No se pudo cancelar"), duration: 4000, color: "danger" });
            }
          },
        },
      ],
    });
  };

  const tarjeta = (s: SolicitudCapacitacion, esAsignada: boolean) => (
    <IonCard key={s.solicitud_id} button onClick={() => abrirDetalle(s)} style={{ margin: "8px 0" }}>
      <IonCardContent>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <strong style={{ color: "#222" }}>N° {s.solicitud_id} · {s.ps_nombre}</strong>
          <EstadoChip estado={s.estado} />
        </div>
        <div style={{ fontSize: 13, marginTop: 4 }}>
          {s.tipo === "INDIVIDUAL" ? `Individual: ${resumenPersonas(s.personas)}` : "Por Punto de Servicio"}
        </div>
        <div style={{ fontSize: 13 }}>
          {s.fecha_programada
            ? `${s.estado === "PROPUESTA" ? "Propuesta" : "Programada"}: ${fmtFechaHora(s.fecha_programada, s.hora_programada)}`
            : `Sugerida: ${fmtFechaHora(s.fecha_sugerida, s.hora_sugerida)}`}
        </div>
        {s.capacitador_nombre && <div style={{ fontSize: 13 }}>Capacitador: {s.capacitador_nombre}</div>}
        {!esAsignada && s.estado === "PROPUESTA" && (
          <div style={{ fontSize: 13, color: "#17a2b8", fontWeight: 600, marginTop: 4 }}>
            Tocá para confirmar la nueva fecha
          </div>
        )}
        {esAsignada && s.puede_cerrar && (
          <IonButton
            size="small"
            style={{ marginTop: 8 }}
            onClick={(e) => {
              e.stopPropagation();
              irACierre(s.solicitud_id);
            }}
          >
            Cerrar capacitación
          </IonButton>
        )}
        {esAsignada && !s.puede_cerrar && s.estado === "PROGRAMADA" && (
          <div style={{ fontSize: 12, color: "#777", marginTop: 4 }}>
            El cierre se habilita el día siguiente a la capacitación.
          </div>
        )}
      </IonCardContent>
    </IonCard>
  );

  const lista = tab === "mis" ? mis : asignadas;

  const fila = (etiqueta: string, valor?: React.ReactNode) => (
    <div style={{ marginBottom: 8 }}>
      <div style={{ fontSize: 11, textTransform: "uppercase", color: "#888" }}>{etiqueta}</div>
      <div style={{ fontSize: 14 }}>{valor === undefined || valor === null || valor === "" ? "-" : valor}</div>
    </div>
  );
  const nombres = (l?: OpcionCatalogo[]) => (l && l.length ? l.map((x) => x.nombre).join(", ") : "-");

  return (
    <IonPage>
      <Header titulo="Solicitudes de Capacitación" />
      <IonContent id="solicitudes-capacitacion">
        <IonRefresher slot="fixed" onIonRefresh={(e) => cargarLista().finally(() => e.detail.complete())}>
          <IonRefresherContent />
        </IonRefresher>
        <div style={{ padding: 16, maxWidth: 800, margin: "0 auto", boxSizing: "border-box" }}>
          {!rol ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <IonSpinner />
            </div>
          ) : (
            <>
              <IonSegment value={tab} onIonChange={(e) => setTab(e.detail.value as Tab)} style={{ marginBottom: 16 }}>
                {rol.solicitante && (
                  <IonSegmentButton value="nueva">
                    <IonLabel>Nueva</IonLabel>
                  </IonSegmentButton>
                )}
                {rol.solicitante && (
                  <IonSegmentButton value="mis">
                    <IonLabel>Mis solicitudes</IonLabel>
                  </IonSegmentButton>
                )}
                {rol.capacitador && (
                  <IonSegmentButton value="asignadas">
                    <IonLabel>Asignadas</IonLabel>
                  </IonSegmentButton>
                )}
              </IonSegment>

              {tab === "nueva" && rol.solicitante && (
                <div>
                  <div style={campoEstilo}>
                    <Etiqueta texto="Dirigido a" />
                    <IonSelect
                      value={dirigidoA}
                      interface="action-sheet"
                      cancelText="Cancelar"
                      onIonChange={(e) => setDirigidoA(e.detail.value)}
                      style={bordeInput}
                      disabled={enviando}
                    >
                      {(catalogo?.dirigido_a || [{ codigo: "OPERATIVO", etiqueta: "Operativos" }]).map((d) => (
                        <IonSelectOption key={d.codigo} value={d.codigo}>
                          {d.etiqueta}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </div>

                  <RadioOpciones
                    etiqueta="Tipo de Capacitación"
                    obligatorio
                    opciones={[
                      { valor: "INDIVIDUAL", etiqueta: "Individual" },
                      { valor: "PS", etiqueta: "Por Punto de Servicio" },
                    ]}
                    valor={tipo}
                    onChange={(v) => {
                      setTipo(v);
                      if (v === "PS") setPersonas([]);
                    }}
                    disabled={enviando}
                  />

                  {tipo === "INDIVIDUAL" && (
                    <div style={campoEstilo}>
                      <Etiqueta texto="Operarios / Supervisores a Capacitar" obligatorio />
                      <BuscadorRemoto<OperarioBusqueda>
                        etiqueta="Buscar por legajo o nombre y agregar"
                        valor={null}
                        onChange={(o) => {
                          if (o) setPersonas((prev) => (prev.some((p) => p.legajo === o.legajo) ? prev : [...prev, o]));
                        }}
                        buscar={async (q) => (await SolicitudCapacitacionAPI.operarios(q)).data.datos || []}
                        getLabel={(o) => `${o.legajo} - ${o.nombre}${o.cargo ? ` (${o.cargo})` : ""}`}
                        getId={(o) => o.legajo}
                        minimo={1}
                        disabled={enviando}
                        limpiarAlElegir
                      />
                      <small style={{ color: "#777" }}>Puede agregar más de una persona</small>
                      {personas.length > 0 && (
                        <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
                          {personas.map((p) => (
                            <Chip
                              key={p.legajo}
                              size="small"
                              label={`${p.legajo} - ${p.nombre}`}
                              onDelete={enviando ? undefined : () => setPersonas((prev) => prev.filter((x) => x.legajo !== p.legajo))}
                              style={{ maxWidth: "100%" }}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div style={campoEstilo}>
                    <Etiqueta texto="Punto de Servicio" obligatorio />
                    <BuscadorRemoto<PuntoServicio>
                      etiqueta="Buscar por nombre del PS"
                      valor={ps}
                      onChange={setPs}
                      buscar={async (q) => (await SolicitudCapacitacionAPI.puntosServicio(q)).data.datos || []}
                      getLabel={(p) => `${p.ps_codigo ? `${p.ps_codigo} - ` : ""}${p.ps_nombre}`}
                      getId={(p) => p.id}
                      disabled={enviando}
                    />
                  </div>

                  <RadioOpciones
                    etiqueta="Autorizado por el Cliente"
                    obligatorio
                    opciones={SI_NO}
                    valor={autorizado}
                    onChange={setAutorizado}
                    disabled={enviando}
                  />

                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <div style={{ ...campoEstilo, flex: "1 1 200px" }}>
                      <Etiqueta texto="Fecha Sugerida" />
                      <IonInput
                        type="date"
                        min={manana}
                        value={fecha}
                        onIonChange={(e) => setFecha(e.detail.value || "")}
                        style={bordeInput}
                        disabled={enviando}
                      />
                      <small style={{ color: "#777" }}>Debe ser posterior a hoy</small>
                    </div>
                    <div style={{ ...campoEstilo, flex: "1 1 200px" }}>
                      <Etiqueta texto="Horario Sugerido" obligatorio />
                      <IonInput
                        type="time"
                        value={hora}
                        onIonChange={(e) => setHora(e.detail.value || "")}
                        style={bordeInput}
                        disabled={enviando}
                      />
                    </div>
                  </div>

                  <MultiSelect<OpcionCatalogo>
                    etiqueta="Capacitación Requerida"
                    obligatorio
                    opciones={catalogo?.temas || []}
                    valor={capacitaciones}
                    onChange={setCapacitaciones}
                    getId={(o) => o.id}
                    getLabel={(o) => o.nombre}
                    getDetalle={(o) => o.categoria || ""}
                    ayuda="Puede seleccionar más de una opción. Puede buscar por tema o categoría (ej. máquinas)"
                    disabled={enviando}
                    cargando={!catalogo}
                  />

                  <MultiSelect<OpcionCatalogo>
                    etiqueta="Documentos Necesarios para el Ingreso del Capacitador"
                    opciones={catalogo?.documentos || []}
                    valor={documentos}
                    onChange={setDocumentos}
                    getId={(o) => o.id}
                    getLabel={(o) => o.nombre}
                    disabled={enviando}
                    cargando={!catalogo}
                  />

                  <div style={campoEstilo}>
                    <Etiqueta texto="Comentarios Adicionales" obligatorio />
                    <IonTextarea
                      value={comentarios}
                      rows={3}
                      autoGrow
                      maxlength={1000}
                      onIonChange={(e) => setComentarios(e.detail.value || "")}
                      style={{ border: "1px solid #ccc", borderRadius: 4, padding: "0 8px" }}
                      disabled={enviando}
                    />
                    <small style={{ color: "#777" }}>
                      Ej. Detallar tipo de máquina, si el operario es nuevo o antiguo, etc.
                    </small>
                  </div>

                  <IonButton expand="block" onClick={enviar} disabled={enviando}>
                    {enviando ? "Enviando..." : "Enviar solicitud"}
                  </IonButton>
                </div>
              )}

              {tab !== "nueva" &&
                (cargandoLista ? (
                  <div style={{ textAlign: "center", padding: 24 }}>
                    <IonSpinner />
                  </div>
                ) : lista.length === 0 ? (
                  <p style={{ textAlign: "center", color: "#777" }}>
                    {tab === "mis" ? "No tenés solicitudes registradas." : "No tenés capacitaciones asignadas."}
                  </p>
                ) : (
                  lista.map((s) => tarjeta(s, tab === "asignadas"))
                ))}
            </>
          )}
        </div>

        {/* Detalle */}
        <IonModal isOpen={!!detalle} onDidDismiss={() => setDetalle(null)}>
          <IonHeader>
            <IonToolbar color="primary">
              <IonTitle>Solicitud N° {detalle?.solicitud_id}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setDetalle(null)}>Cerrar</IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent>
            {detalle && (
              <div style={{ padding: 16, maxWidth: 700, margin: "0 auto" }}>
                <div style={{ marginBottom: 12 }}>
                  <EstadoChip estado={detalle.estado} />
                </div>
                {fila("Punto de Servicio", `${detalle.ps_codigo ? `${detalle.ps_codigo} - ` : ""}${detalle.ps_nombre}`)}
                {fila(
                  "Tipo",
                  detalle.tipo === "INDIVIDUAL"
                    ? `Individual: ${(detalle.personas || []).map((p) => `${p.legajo} - ${p.nombre}`).join(", ")}`
                    : "Por Punto de Servicio"
                )}
                {fila("Autorizado por el cliente", detalle.autorizado_cliente ? "Sí" : "No")}
                {fila("Fecha / horario sugerido", fmtFechaHora(detalle.fecha_sugerida, detalle.hora_sugerida))}
                {detalle.fecha_programada &&
                  fila(
                    detalle.estado === "PROPUESTA" ? "Fecha / horario propuesto" : "Fecha / horario programado",
                    fmtFechaHora(detalle.fecha_programada, detalle.hora_programada)
                  )}
                {fila("Capacitador", detalle.capacitador_nombre)}
                {fila("Solicitante", detalle.solicitante_nombre)}
                {fila("Capacitación requerida", nombres(detalle.items?.REQUERIDA))}
                {fila("Documentos necesarios", nombres(detalle.items?.DOCUMENTO))}
                {fila("Comentarios adicionales", detalle.comentarios)}
                {detalle.comentario_coordinador && fila("Comentario del coordinador", detalle.comentario_coordinador)}
                {detalle.comentario_solicitante && fila("Respuesta del solicitante", detalle.comentario_solicitante)}
                {detalle.motivo_rechazo && fila("Motivo de rechazo", detalle.motivo_rechazo)}

                {detalle.estado === "CERRADA" && (
                  <div style={{ borderTop: "1px solid #ddd", paddingTop: 12 }}>
                    {fila("Capacitación realizada", detalle.realizada ? "Sí" : "No")}
                    {detalle.realizada ? (
                      <>
                        {fila("Fecha realizada", fmtFecha(detalle.fecha_realizada))}
                        {fila("Temas impartidos", nombres(detalle.temas_impartidos))}
                        {fila(
                          `Capacitados (${(detalle.capacitados || []).length})`,
                          <TablaPersonas personas={detalle.capacitados || []} />
                        )}
                        {fila("Principales debilidades", nombres(detalle.items?.DEBILIDAD))}
                        {fila("Comentarios del capacitador", detalle.comentarios_cierre)}
                      </>
                    ) : (
                      fila("Motivo", detalle.motivo_no_realizada)
                    )}
                  </div>
                )}

                {detalle.estado === "PROPUESTA" && detalle.solicitante_cedula && tab === "mis" && (
                  <div style={{ borderTop: "1px solid #ddd", paddingTop: 12 }}>
                    <p style={{ fontSize: 14 }}>
                      El coordinador propone realizar la capacitación el{" "}
                      <strong>{fmtFechaHora(detalle.fecha_programada, detalle.hora_programada)}</strong>.
                    </p>
                    {!rechazando ? (
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <IonButton color="success" disabled={respondiendo} onClick={() => responder(true)}>
                          Aceptar fecha
                        </IonButton>
                        <IonButton color="medium" fill="outline" disabled={respondiendo} onClick={() => setRechazando(true)}>
                          No acepto
                        </IonButton>
                      </div>
                    ) : (
                      <>
                        <div style={campoEstilo}>
                          <Etiqueta texto="Motivo / comentario para el coordinador" obligatorio />
                          <IonTextarea
                            value={respComentario}
                            rows={2}
                            autoGrow
                            maxlength={1000}
                            onIonChange={(e) => setRespComentario(e.detail.value || "")}
                            style={{ border: "1px solid #ccc", borderRadius: 4, padding: "0 8px" }}
                          />
                        </div>
                        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                          <div style={{ ...campoEstilo, flex: "1 1 180px" }}>
                            <Etiqueta texto="Nueva fecha sugerida (opcional)" />
                            <IonInput
                              type="date"
                              min={manana}
                              value={respFecha}
                              onIonChange={(e) => setRespFecha(e.detail.value || "")}
                              style={bordeInput}
                            />
                          </div>
                          <div style={{ ...campoEstilo, flex: "1 1 180px" }}>
                            <Etiqueta texto="Nuevo horario sugerido (opcional)" />
                            <IonInput
                              type="time"
                              value={respHora}
                              onIonChange={(e) => setRespHora(e.detail.value || "")}
                              style={bordeInput}
                            />
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <IonButton disabled={respondiendo} onClick={() => responder(false)}>
                            Enviar al coordinador
                          </IonButton>
                          <IonButton fill="clear" disabled={respondiendo} onClick={() => setRechazando(false)}>
                            Volver
                          </IonButton>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {tab === "asignadas" &&
                  (detalle.estado === "PROGRAMADA" || detalle.estado === "PENDIENTE_CIERRE") &&
                  (detalle.fecha_programada && detalle.fecha_programada < hoy ? (
                    <IonButton
                      expand="block"
                      style={{ marginTop: 16 }}
                      onClick={() => {
                        const solicitudId = detalle.solicitud_id;
                        setDetalle(null);
                        irACierre(solicitudId);
                      }}
                    >
                      Cerrar capacitación
                    </IonButton>
                  ) : (
                    <p style={{ fontSize: 13, color: "#777", marginTop: 16 }}>
                      El cierre se habilita el día siguiente a la capacitación.
                    </p>
                  ))}

                {tab === "mis" && (detalle.estado === "PENDIENTE" || detalle.estado === "PROPUESTA") && (
                  <IonButton color="danger" fill="outline" expand="block" style={{ marginTop: 16 }} onClick={cancelar}>
                    Cancelar solicitud
                  </IonButton>
                )}
              </div>
            )}
          </IonContent>
        </IonModal>
      </IonContent>
    </IonPage>
  );
};

export default SolicitudesCapacitacion;
